import { keepPreviousData, queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ApiError } from './client';
import { agentApi, authApi, evidenceApi, orgApi, policyApi } from './endpoints';
import type { AlertFilters, EventFilters, Me } from './types';

/** Query keys, in one place so invalidation stays honest. */
export const qk = {
  me: ['me'] as const,
  portfolio: ['portfolio'] as const,
  org: (orgId: string) => ['org', orgId] as const,
  members: (orgId: string) => ['org', orgId, 'members'] as const,
  invites: (orgId: string) => ['org', orgId, 'invites'] as const,
  agents: (orgId: string) => ['org', orgId, 'agents'] as const,
  agent: (orgId: string, agentId: string) => ['org', orgId, 'agents', agentId] as const,
  tokens: (orgId: string) => ['org', orgId, 'enrollment-tokens'] as const,
  rules: (orgId: string) => ['org', orgId, 'rules'] as const,
  overview: (orgId: string) => ['org', orgId, 'overview'] as const,
  events: (orgId: string, f?: EventFilters) => ['org', orgId, 'events', f ?? {}] as const,
  event: (orgId: string, eventId: string) => ['org', orgId, 'event', eventId] as const,
  proof: (orgId: string, eventId: string) => ['org', orgId, 'proof', eventId] as const,
  alerts: (orgId: string, f?: AlertFilters) => ['org', orgId, 'alerts', f ?? {}] as const,
  alert: (orgId: string, alertId: string) => ['org', orgId, 'alert', alertId] as const,
  anchors: (orgId: string) => ['org', orgId, 'anchors'] as const,
  shares: (orgId: string) => ['org', orgId, 'shares'] as const,
  notificationEmails: (orgId: string) => ['org', orgId, 'notification-emails'] as const,
  shared: (token: string) => ['shared', token] as const,
  invite: (token: string) => ['invite', token] as const,
};

/**
 * The signed-in user, or null when signed out (401 is an answer, not an error).
 * Shared with the router, which starts it before a screen's code has loaded.
 */
export const meQuery = queryOptions<Me | null>({
  queryKey: qk.me,
  queryFn: async () => {
    try {
      return await authApi.me();
    } catch (err) {
      if (err instanceof ApiError && err.isUnauthorized) return null;
      throw err;
    }
  },
  staleTime: 60_000,
});

export const orgQuery = (orgId: string) => queryOptions({ queryKey: qk.org(orgId), queryFn: () => orgApi.get(orgId) });

export const useMe = () => useQuery(meQuery);
export const useOrg = (orgId: string) => useQuery(orgQuery(orgId));
export const useMembers = (orgId: string) => useQuery({ queryKey: qk.members(orgId), queryFn: () => orgApi.members(orgId) });
export const useInvites = (orgId: string, enabled = true) =>
  useQuery({ queryKey: qk.invites(orgId), queryFn: () => orgApi.invites(orgId), enabled });

export const useAgents = (orgId: string) =>
  useQuery({ queryKey: qk.agents(orgId), queryFn: () => agentApi.list(orgId), refetchInterval: 30_000 });
export const useAgent = (orgId: string, agentId: string) =>
  useQuery({ queryKey: qk.agent(orgId, agentId), queryFn: () => agentApi.get(orgId, agentId) });
export const useEnrollmentTokens = (orgId: string, enabled = true) =>
  useQuery({ queryKey: qk.tokens(orgId), queryFn: () => agentApi.enrollmentTokens(orgId), enabled });
export const useRules = (orgId: string) => useQuery({ queryKey: qk.rules(orgId), queryFn: () => policyApi.list(orgId) });

/** The templates this deployment offers. They change only when it is updated. */
export const usePolicyTemplates = () =>
  useQuery({ queryKey: ['policy-templates'], queryFn: () => policyApi.templates(), staleTime: Infinity });

/**
 * The console is a monitoring screen, so the things that change on their own
 * poll on their own. Someone watching for an alert should not have to know that
 * clicking away and back is what fetches it.
 *
 * `refetchIntervalInBackground` is deliberate. React Query stops polling a
 * hidden document by default, which is right for most pages and wrong for this
 * one: a console left on a second monitor, behind a terminal, or shared over a
 * call would quietly stop updating and show nothing to say so. For a screen
 * whose promise is that you will know, stale and silent is the one state it
 * must not have.
 */
const LIVE = { refetchInterval: 15_000, refetchIntervalInBackground: true } as const;

export const useOverview = (orgId: string) =>
  useQuery({ queryKey: qk.overview(orgId), queryFn: () => evidenceApi.overview(orgId), ...LIVE });

/**
 * Every organization the user belongs to, with what needs attention in each.
 * LIVE for the same reason as the overview, and more so: this is the screen
 * left open to find out whether anything is wrong anywhere.
 */
export const usePortfolio = () => useQuery({ queryKey: qk.portfolio, queryFn: () => authApi.portfolio(), ...LIVE });

export function useEvents(orgId: string, filters: EventFilters) {
  return useInfiniteQuery({
    queryKey: qk.events(orgId, filters),
    queryFn: ({ pageParam }) => evidenceApi.events(orgId, filters, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
  });
}

export const useEvent = (orgId: string, eventId: string | null) =>
  useQuery({
    queryKey: qk.event(orgId, eventId ?? ''),
    queryFn: () => evidenceApi.event(orgId, eventId!),
    enabled: !!eventId,
  });

export const useProof = (orgId: string, eventId: string) =>
  useQuery({ queryKey: qk.proof(orgId, eventId), queryFn: () => evidenceApi.proof(orgId, eventId), staleTime: 15_000 });

export function useAlerts(orgId: string, filters: AlertFilters) {
  return useInfiniteQuery({
    queryKey: qk.alerts(orgId, filters),
    queryFn: ({ pageParam }) => evidenceApi.alerts(orgId, filters, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
    ...LIVE,
  });
}

export const useAlert = (orgId: string, alertId: string | undefined) =>
  useQuery({
    queryKey: qk.alert(orgId, alertId ?? ''),
    queryFn: () => evidenceApi.alert(orgId, alertId!),
    enabled: !!alertId,
  });

export function useAnchors(orgId: string) {
  return useInfiniteQuery({
    queryKey: qk.anchors(orgId),
    queryFn: ({ pageParam }) => evidenceApi.anchors(orgId, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: 30_000,
  });
}

export const useShares = (orgId: string) => useQuery({ queryKey: qk.shares(orgId), queryFn: () => evidenceApi.shares(orgId) });

export const useNotificationEmails = (orgId: string) =>
  useQuery({ queryKey: qk.notificationEmails(orgId), queryFn: () => orgApi.notificationEmails(orgId) });

export const useShared = (token: string) =>
  useQuery({ queryKey: qk.shared(token), queryFn: () => evidenceApi.shared(token), retry: false });

export const useInvitePreview = (token: string) =>
  useQuery({ queryKey: qk.invite(token), queryFn: () => orgApi.previewInvite(token), retry: false });
